/**
 * Format number in Indian Numbering Format (lakhs/crores comma grouping)
 *
 * Default (as of 2026-04-23): rounds to the **nearest rupee** — no decimals
 * shown. This matches the user directive that totals, invested values,
 * current values, P/L and dividend income should be clean rupee figures
 * across the app. Price-type values (per-share prices, avg cost, NAV,
 * brokerage / STT / GST break-up columns) should explicitly pass
 * `{ decimals: 2 }` so their paisa precision is preserved.
 *
 * Privacy mode (2026-05-25): when the user toggles "Privacy mode" on
 * (see PrivacyContext), every figure rendered through formatINR /
 * formatPercentage is replaced with bullet placeholders so over-the-
 * shoulder snooping is impossible. Names / strings stay visible.
 * PrivacyContext is the source of truth — it writes to this module's
 * `_privateMode` flag whenever the toggle flips.
 */

// Module-level mask state. PrivacyContext owns lifecycle; we just read
// the flag at format time. Lives outside React state on purpose so plain
// .map() / .reduce() / non-component utility code (downloads, exports)
// that call formatINR directly also pick it up.
let _privateMode = false;
const _listeners = new Set();

export const setPrivateMode = (on) => {
  _privateMode = !!on;
  _listeners.forEach((fn) => {
    try { fn(_privateMode); } catch (_) { /* ignore */ }
  });
};

export const isPrivateMode = () => _privateMode;

export const subscribePrivateMode = (fn) => {
  _listeners.add(fn);
  return () => _listeners.delete(fn);
};

// Visual placeholder when masked. Width roughly mimics a typical rupee
// figure so layouts don't shift much.
const MASK_TEXT = '•••••';

const addIndianCommas = (numStr) => {
  const [intPart, decPart] = numStr.split('.');
  const lastThree = intPart.slice(-3);
  const rest = intPart.slice(0, -3);
  const formatted = rest
    ? rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' + lastThree
    : lastThree;
  return decPart !== undefined ? `${formatted}.${decPart}` : formatted;
};

export const formatINR = (value, options = {}) => {
  // `abbreviate` defaults to FALSE so every amount on every screen shows
  // the exact rupee figure with Indian grouping — users can always see
  // the precise number at a glance. Only opt-in via the options arg when
  // a card is genuinely too narrow.
  //
  // `decimals` defaults to 0 (rupee-only). Pass `decimals: 2` at price /
  // avg-cost / NAV / break-up-column call sites to retain paisa precision.
  //
  // `forceUnmask: true` — bypass the global Privacy mask. Used ONLY by
  // <MaskedFigure> during a long-press reveal. Plain text callers should
  // never set this.
  const {
    abbreviate = false, showSign = false, decimals = 0, forceUnmask = false,
  } = options;
  if (value === null || value === undefined || isNaN(value)) return '₹0';
  if (_privateMode && !forceUnmask) return `₹${MASK_TEXT}`;

  const absValue = Math.abs(value);
  const sign = value < 0 ? '-' : showSign && value > 0 ? '+' : '';

  if (abbreviate && absValue >= 10000000) {
    return `${sign}₹${(absValue / 10000000).toFixed(2)}Cr`;
  }
  if (abbreviate && absValue >= 100000) {
    return `${sign}₹${(absValue / 100000).toFixed(2)}L`;
  }
  return `${sign}₹${addIndianCommas(absValue.toFixed(decimals))}`;
};

export const formatPercentage = (value, showSign = false, options = {}) => {
  if (value === null || value === undefined || isNaN(value)) return '0.00%';
  const { forceUnmask = false } = options;
  if (_privateMode && !forceUnmask) return `${MASK_TEXT}%`;
  const sign = showSign && value > 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}%`;
};

/**
 * Format a share / unit quantity in Indian numbering, with privacy-mask
 * support. Use this at every call site that previously did
 *   `Number(qty).toLocaleString('en-IN', { maximumFractionDigits: N })`
 * so Privacy mode can hide the figure consistently.
 *
 * `value` may be a number or a numeric string. Null / NaN → '—'.
 *
 * Options:
 *   maximumFractionDigits — default 3 (matches MF unit precision). Pass 0
 *                           for whole-share equity quantities.
 */
export const formatQuantity = (value, options = {}) => {
  const { maximumFractionDigits = 3, forceUnmask = false } = options;
  if (value === null || value === undefined || value === '') return '—';
  const num = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(num)) return '—';
  if (_privateMode && !forceUnmask) return MASK_TEXT;
  return num.toLocaleString('en-IN', { maximumFractionDigits });
};
